import React, { useEffect, useState } from "react";
import { createChannel, getChannelMessages, getTeamsChannels, sendMessageToChannel } from "../utils/TeamsApi";
import {
    EmptyState,
    FormField,
    FormFieldLabel,
    HEADER_HEIGHT_DESKTOP,
    LoadingState,
    Message,
    MuiChat
} from "@lectra/mui-library";
import { Side } from "@lectra/mui-library/types/components/chat/displayer";
import { useAccount, useMsal } from "@azure/msal-react";
import { Alert, Box, Button, FormControl, MenuItem, Select, Snackbar, TextField, Typography } from "@mui/material";

const BANNER_HEIGHT = 64

const SendMessage: React.FC = () => {
    const {accounts} = useMsal();
    const account = useAccount(accounts[0] || {});

    const [channels, setChannels] = useState<any[]>([]);
    const [selectedChannel, setSelectedChannel] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [filterText, setFilterText] = useState("poc");
    const [messages, setMessages] = useState<any[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState<string>("");
    const [newChannelName, setNewChannelName] = useState("");
    const [newChannelDesc, setNewChannelDesc] = useState("");
    const [creatingChannel, setCreatingChannel] = useState(false);
    const [muiMessages, setMuiMessages] = useState<Message[]>([])
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [name, setName] = useState("");

    useEffect(() => {
        getTeamsChannels(filterText).then(setChannels).catch(err => setError(err.message));
    }, []);

    useEffect(() => {
        const fetchMessages = async () => {
            setLoadingMessages(true);
            setMessages([]);
            setError(null);
            try {
                const channel = channels.find(ch => ch.id === selectedChannel);
                if (channel) {
                    const msgs = await getChannelMessages(channel.teamId, channel.id);
                    setMessages(msgs);
                }
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoadingMessages(false);
            }
        };
        if (selectedChannel) {
            void fetchMessages();
        }
    }, [selectedChannel, channels]);

    useEffect(() => {
        if (account && account.name) {
            setName(account.name);
        } else {
            setName("");
        }
    }, [account]);


    useEffect(() => {
        if (messages.length > 0) {
            setMuiMessages(messages.map((msg, i) => (
                {
                    id: msg.id || i,
                    content: msg.body?.content || "no content",
                    user: {
                        id: msg.from?.user?.displayName || 'Unknown',
                        fullName: msg.from?.user?.displayName || 'Unknown'
                    },
                    date: new Date(Date.parse(msg.createdDateTime)) || undefined,
                    side: msg.from?.user?.displayName === name ? "RIGHT" as Side : "LEFT" as Side
                }
            )).reverse())
        }
    }, [messages]);

    useEffect(() => {
        if (error) {
            setOpenSnackbar(true)
        } else if (error === null) {
            setOpenSnackbar(false)
        }
    }, [error]);

    // Extract teams from channels
    const teams = Array.from(new Set(channels.map(ch => JSON.stringify({
        teamId: ch.teamId,
        teamName: ch.teamName
    })))).map(str => JSON.parse(str));

    const handleRefresh = async () => {
        setLoading(true);
        setError(null);
        try {
            const filteredChannels = await getTeamsChannels(filterText);
            setChannels(filteredChannels);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async (msg: string) => {
        setLoading(true);
        setSuccess(null);
        setError(null);
        try {
            const channel = channels.find(ch => ch.id === selectedChannel);
            if (!channel) throw new Error("Channel not found");
            await sendMessageToChannel(channel.teamId, channel.id, msg);
            setSuccess("Message sent!");
            setOpenSnackbar(true)
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
            void handleReloadMessages()
        }
    };

    const handleReloadMessages = async () => {
        if (!selectedChannel) return;
        setLoadingMessages(true);
        setError(null);
        try {
            const channel = channels.find(ch => ch.id === selectedChannel);
            if (channel) {
                const msgs = await getChannelMessages(channel.teamId, channel.id);
                setMessages(msgs);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleCreateChannel = async () => {
        if (!selectedTeamId || !newChannelName) return;
        setCreatingChannel(true);
        setError(null);
        try {
            await createChannel(selectedTeamId, newChannelName, newChannelDesc);
            setNewChannelName("");
            setNewChannelDesc("");
            // Refresh channels list
            const filteredChannels = await getTeamsChannels(filterText);
            setChannels(filteredChannels);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setCreatingChannel(false);
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <Box sx={(theme) => ({
            display: "flex",
            height: `calc(100dvh - ${HEADER_HEIGHT_DESKTOP}px - ${BANNER_HEIGHT + 1}px - 2 * ${theme.spacing(
                3,
            )} - 2 * ${theme.spacing(2)} - 2px)`
        })}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    mr: 2,
                    gap: 4,
                    overflowY: "auto",
                }}
            >
                <Box>
                    <Typography variant="h3">Send a message to a Teams channel</Typography>
                    <Typography>Filter:</Typography>
                    <Box sx={{display: "flex", alignItems: "center", mb: 2, gap: 1}}>
                        <TextField
                            placeholder="Filter teams..."
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                            fullWidth
                        />
                        <Button variant='outlined'
                                color='secondary'
                                onClick={handleRefresh}
                                disabled={loading}>
                            Refresh
                        </Button>
                    </Box>
                    <Typography>Channel:</Typography>
                    <FormControl sx={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Select
                            value={selectedChannel}
                            onChange={e => setSelectedChannel(e.target.value)}
                            required
                            sx={{
                                "& .MuiSelect-select .notranslate::after": {
                                    content: `"Select a channel"`,
                                    opacity: 0.42,
                                }
                            }}
                        >
                            {channels.map(ch => (
                                <MenuItem key={ch.id} value={ch.id}>
                                    {ch.teamName} - {ch.displayName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <Typography variant="h3">Create a channel in a Team</Typography>
                    <FormField
                        label={
                            <FormFieldLabel>
                                Team:
                            </FormFieldLabel>
                        }
                    >
                        <FormControl sx={{width: "100%", display: "flex", justifyContent: "center"}}>
                            <Select
                                value={selectedTeamId}
                                onChange={e => setSelectedTeamId(e.target.value)}
                                required
                                sx={{
                                    "& .MuiSelect-select .notranslate::after": {
                                        content: `"Select a team"`,
                                        opacity: 0.42,
                                    }
                                }}
                            >
                                {teams.map(team => (
                                    <MenuItem key={team.teamId} value={team.teamId}>
                                        {team.teamName}
                                    </MenuItem>))}
                            </Select>
                        </FormControl>
                    </FormField>
                    <FormField
                        label={<FormFieldLabel>Channel name:</FormFieldLabel>}
                    >
                        <FormControl sx={{width: "100%", display: "flex", justifyContent: "center"}}>
                            <TextField
                                value={newChannelName}
                                onChange={e => setNewChannelName(e.target.value)}
                                required
                                fullWidth
                            />
                        </FormControl>
                    </FormField>
                    <FormField
                        label={<FormFieldLabel>Description:</FormFieldLabel>}
                    >
                        <FormControl sx={{width: "100%", display: "flex", justifyContent: "center"}}>
                            <TextField
                                value={newChannelDesc}
                                onChange={e => setNewChannelDesc(e.target.value)}
                                fullWidth
                            />
                        </FormControl>
                    </FormField>
                    <Box sx={{display: "flex", justifyContent: 'center', width: "100%", mt: 1}}>
                        <Button onClick={handleCreateChannel}
                                disabled={creatingChannel || !selectedTeamId || !newChannelName}>
                            {creatingChannel ? "Creating..." : "Create Channel"}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    height: "100%",
                    borderLeft: 1,
                    borderColor: "divider",
                    borderBottomRightRadius: 8,
                    display: "flex",
                    flexDirection: "column",
                    flex: 2,
                }}
            >
                <Box sx={{display: "flex", justifyContent: 'center', width: "100%",}}>
                    <Button variant='outlined'
                            color='secondary'
                            onClick={handleReloadMessages}
                            disabled={loadingMessages || !selectedChannel}
                            sx={{mb: 2}}>
                        Reload conversation
                    </Button>
                </Box>
                <Box sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflow: "hidden",
                    overflowY: "auto",
                    backgroundColor: "background.navigation",
                }}>
                    {loadingMessages && <LoadingState title="Loading messages..."/>}
                    {!loadingMessages && messages.length === 0 && selectedChannel ?
                        <EmptyState title="No messages found."
                                    sx={{flex: 1, height: "auto", minHeight: "auto", mt: 0}}/> :
                        <MuiChat
                            messageDisplayerProps={{
                                messages: muiMessages,
                            }}
                            messageSenderProps={{
                                chatInputPlaceholderText: "Write a message ...",
                                onSendMessage: (message) => handleSend(message),
                            }}
                        />}
                    {success && <Snackbar
                        open={openSnackbar}
                        autoHideDuration={2000}
                        onClose={handleCloseSnackbar}
                    >
                        <Alert severity="success">{success}</Alert>
                    </Snackbar>}
                    {error && <Snackbar
                        open={openSnackbar}
                        autoHideDuration={2000}
                        onClose={handleCloseSnackbar}
                    >
                        <Alert severity="error">{error}</Alert>
                    </Snackbar>}
                </Box>
            </Box>
        </Box>

    );
};

export default SendMessage;
